/**
 * FinanzasDashboardPage.jsx — Panel principal de FinApp
 * Ruta: /finanzas
 */
import { useState, useEffect } from 'react';
import finanzasApi from '../../lib/finanzas_api';
import { Building2, CreditCard, ArrowLeftRight, TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';
import FinAppLayout from '../../components/FinAppLayout';
import { useAuth } from '../../App';

const PURPLE = '#6C5ECF';

const fmt = (n) =>
  new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);

export default function FinanzasDashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    finanzasApi.get('/dashboard')
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const cards = stats ? [
    { label: 'Entidades',   value: stats.entidades_count,  icon: Building2,     color: '#6366f1', bg: '#EEF2FF' },
    { label: 'Cuentas',     value: stats.cuentas_count,   icon: CreditCard,    color: '#8B5CF6', bg: '#F5F3FF' },
    { label: 'Movimientos', value: stats.movimientos_count,icon: ArrowLeftRight, color: '#F59E0B', bg: '#FFFBEB' },
    { label: 'Ingresos',    value: `$ ${fmt(stats.total_ingresos)}`, icon: TrendingUp, color: '#10B981', bg: '#ECFDF5' },
    { label: 'Egresos',     value: `$ ${fmt(stats.total_egresos)}`,  icon: TrendingDown,color: '#EF4444', bg: '#FEF2F2' },
    { label: 'Balance',     value: `$ ${fmt(stats.balance)}`, icon: BarChart3,
      color: stats.balance >= 0 ? PURPLE : '#EF4444',
      bg: stats.balance >= 0 ? '#F3F0FF' : '#FEF2F2' },
  ] : [];

  return (
    <FinAppLayout>
      <div data-testid="finanzas-dashboard-page">
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#111827', letterSpacing: '-0.025em', margin: 0 }}>Panel</h1>
          <p style={{ marginTop: '6px', color: '#6B7280', fontSize: '14px' }}>
            Bienvenido{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''} · Resumen financiero
          </p>
        </div>

        {/* KPI Grid */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} style={{ height: '90px', background: '#F9FAFB', borderRadius: '12px', border: '1px solid #F3F4F6' }} />
            ))}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
            {cards.map(c => (
              <div key={c.label} data-testid={`stat-card-${c.label.toLowerCase()}`}
                style={{
                  background: '#FFFFFF', border: '1px solid #F3F4F6', borderRadius: '12px',
                  padding: '18px 20px', display: 'flex', alignItems: 'flex-start',
                  justifyContent: 'space-between', gap: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  transition: 'box-shadow 0.15s ease, transform 0.15s ease',
                  cursor: 'default',
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div>
                  <p style={{ fontSize: '11px', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>{c.label}</p>
                  <p style={{ fontSize: '1.375rem', fontWeight: 700, color: '#111827', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{c.value}</p>
                </div>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <c.icon size={16} color={c.color} strokeWidth={1.75} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Mes actual */}
        {stats && (stats.ingresos_mes > 0 || stats.gastos_mes > 0) && (
          <div style={{ marginTop: '28px', background: '#FAFAFA', border: '1px solid #F3F4F6', borderRadius: '12px', padding: '20px 24px' }}>
            <p style={{ fontSize: '10px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '14px' }}>Este mes</p>
            <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
              {[
                { label: 'Ingresos', value: stats.ingresos_mes, color: '#10B981' },
                { label: 'Gastos', value: stats.gastos_mes, color: '#EF4444' },
                { label: 'Balance', value: stats.balance_mes, color: stats.balance_mes >= 0 ? PURPLE : '#EF4444' },
              ].map(m => (
                <div key={m.label}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF' }}>{m.label}</p>
                  <p style={{ fontSize: '1.125rem', fontWeight: 700, color: m.color, fontVariantNumeric: 'tabular-nums', marginTop: '2px' }}>$ {fmt(m.value)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {stats && stats.entidades_count === 0 && (
          <div data-testid="empty-finanzas-hint" style={{ marginTop: '60px', textAlign: 'center' }}>
            <div style={{ width: '56px', height: '56px', background: '#F3F0FF', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Building2 size={22} color={PURPLE} strokeWidth={1.5} />
            </div>
            <p style={{ fontSize: '15px', fontWeight: 600, color: '#374151' }}>Comienza creando una entidad</p>
            <p style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '6px' }}>
              Ve a <strong style={{ color: PURPLE }}>Entidades</strong> para agregar tu primera empresa o persona.
            </p>
          </div>
        )}
      </div>
    </FinAppLayout>
  );
}
